/**
 * Long-turn reassurance flashes.
 *
 * Once a turn has been running for ≥ 3 minutes, flash a "long analysis
 * in progress" message over the activity row for exactly 3 seconds,
 * once per minute. Tool-call activity remains the primary surface the
 * whole time — the flashes are brief interludes that acknowledge the
 * wait without replacing the live tool updates.
 *
 * Returns a controller with start/stop methods so the caller can drive
 * the timer from inside an async function (the `sendMessage` flow)
 * without having to thread state through useEffect.
 */

import { useChatStore } from "@stores/chatStore";
import { useCallback, useRef } from "react";

// Lowered from 180s → 60s. With the activity timeline + per-entry elapsed
// counter giving constant feedback, the reassurance string is no longer
// the user's only signal that the agent isn't frozen — it just sits in
// for the rare quiet stretches between tool batches.
const LONG_TURN_THRESHOLD_MS = 60_000;
const STATUS_FLASH_DURATION_MS = 3_000;
const STATUS_ROTATION_INTERVAL_MS = 45_000;
const LONG_TURN_STATUS =
  "Still working — multi-tool turns can take a minute or two.";

export interface LongTurnStatusController {
  /** Begin the 1-minute polling rotation; flashes once the threshold
   *  is reached and again every minute until `stop()`. */
  start: () => void;
  /** Tear down the rotation and clear any pending flashes. Idempotent. */
  stop: () => void;
}

export function useLongTurnStatus(): LongTurnStatusController {
  const setTransientStatus = useChatStore((s) => s.setTransientStatus);
  const rotationRef = useRef<number | null>(null);
  const pendingFlashesRef = useRef<number[]>([]);
  const turnStartedAtRef = useRef<number>(0);

  const stop = useCallback(() => {
    if (rotationRef.current !== null) {
      window.clearInterval(rotationRef.current);
      rotationRef.current = null;
    }
    for (const id of pendingFlashesRef.current) {
      window.clearTimeout(id);
    }
    pendingFlashesRef.current = [];
    setTransientStatus(null);
  }, [setTransientStatus]);

  const start = useCallback(() => {
    // Restart cleanly even if the previous turn didn't tear down (e.g.
    // stop-streaming during the rotation).
    stop();
    turnStartedAtRef.current = Date.now();
    rotationRef.current = window.setInterval(() => {
      const elapsed = Date.now() - turnStartedAtRef.current;
      if (elapsed < LONG_TURN_THRESHOLD_MS) return;
      setTransientStatus(LONG_TURN_STATUS);
      const clearId = window.setTimeout(() => {
        setTransientStatus(null);
      }, STATUS_FLASH_DURATION_MS);
      pendingFlashesRef.current.push(clearId);
    }, STATUS_ROTATION_INTERVAL_MS);
  }, [stop, setTransientStatus]);

  return { start, stop };
}
